import { Badge, Divider, Drawer, type DrawerProps, Flex, Grid, Text } from "@mantine/core";
import React, { type JSX } from "react";

import { useChallenges } from "@/sales-tools/challenges/api/useChallenges";
import { ChallengeProgression } from "@/sales-tools/challenges/component/ChallengeProgression";
import { findFirstActiveChallenge } from "@/sales-tools/challenges/utils/findFirstActiveChallenge";
import { useLogger } from "@/sdk";

import { getChallengeDescription } from "../utils/getChallengeDescription";
import { getDaysLeftText } from "../utils/getDaysLeftText";
import { ChallengeBadge } from "./ChallengeBadge";
import classes from "./ChallengeDrawer.module.css";

export function ChallengeDrawer(props: DrawerProps): JSX.Element | null {
    const logger = useLogger({ module: "ChallengeDrawer", supportTeam: "sales" });
    const { data, isError, isLoading } = useChallenges();

    if (isError || isLoading || !data) {
        return null;
    }
    const activeChallenge = findFirstActiveChallenge(logger, data);
    if (!activeChallenge) return null;

    const { earnedBadges = [], details, challengeType, badgeType: achievableBadgeType } = activeChallenge;
    const { height, width } = getBadgeSize(achievableBadgeType);
    return (
        <Drawer
            classNames={{
                header: classes.drawerBackground,
                content: classes.drawerBackground,
                body: classes.drawerBody,
            }}
            size={challengeType === "solo" ? "xs" : "md"}
            {...props}
            withCloseButton={false}
            style={{
                position: "absolute",
                zIndex: "var(--mantine-priority-highest)",
            }}
            styles={{ inner: { marginTop: "3.5rem" } }}
        >
            <Flex direction="column" p="lg">
                <Flex>
                    <Text fw="bold">{details.length === 1 ? "Solo" : "Team"} challenge</Text>
                    <Drawer.CloseButton color="dark.1" />
                </Flex>
                <Text c="dark.2" size="xs">
                    {getChallengeDescription(logger, activeChallenge)}
                </Text>
                <Badge radius="xs" bg="primary.2" c="dark.9" fw={400} className={classes.badge} mt="xs">
                    {`${getDaysLeftText(activeChallenge)} left`}
                </Badge>
                <Flex gap="xl">
                    <ChallengeProgression displayDirection="horizontal" challenge={activeChallenge} />
                </Flex>
            </Flex>
            <Divider my="md" color="dark.4" />
            <Flex p="lg" align="center" direction="column" gap="xs">
                <ChallengeBadge badgeType={achievableBadgeType} w={width} h={height} />
                <Text>Complete to earn a new badge!</Text>
            </Flex>
            <Divider my="md" color="dark.4" />
            <Flex p="lg" direction="column" gap="sm">
                <Text>Trophy case</Text>
                <Grid mt="sm">
                    {Array.from({ length: 8 }, (_, i) => {
                        const type = earnedBadges.length !== i ? earnedBadges[i]?.badgeType : achievableBadgeType;
                        return (
                            <Grid.Col span={3} key={`${i}-${type}`}>
                                <Flex justify="center">
                                    <ChallengeBadge
                                        w={48}
                                        h={48}
                                        badgeType={type}
                                        opacity={earnedBadges?.length === i ? "20%" : undefined}
                                    />
                                </Flex>
                            </Grid.Col>
                        );
                    })}
                </Grid>
            </Flex>
        </Drawer>
    );
}

function getBadgeSize(badgeType: string): { height: number; width: number } {
    switch (badgeType) {
        case "SOLO_ANIMATED_BADGE":
        case "TEAM_ANIMATED_BADGE":
            return {
                height: 96,
                width: 96,
            };
        default:
            return {
                height: 48,
                width: 48,
            };
    }
}
